import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch, onMounted } from "vue";
import { useServices, types } from "@/lib/openapi";
import { RouteParamsGeneric, useRoute } from "vue-router";
import { useWorktreeStore } from "@/composables/worktree-manager";
import { SyncReturn, useSyncStore } from "./sync-store";
import { syncChannel } from "@/lib/broadcast-channels";
import { snakeCase } from "lodash";
import { useSteksManager } from "./steks-store";
import { until } from "@vueuse/core";
import { useToast } from "primevue/usetoast";

export type EntityInfo = types.EntityInfo;
export type EntityType = types.EntityType;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const EntityType = types.EntityType;
export type ProductInTree = types.ProductInTree;
export type ProcessInTree = types.ProcessInTree;

/**
 * Check if is valid EntityType key.
 */
function isEntityType(value: string): value is keyof typeof EntityType {
    return Object.keys(EntityType).includes(value);
}

/**
 * Convert a string into an EntityType.
 */
function getEntityType(value: string): EntityType {
    if (isEntityType(value)) {
        return EntityType[value];
    }
    throw new Error(`Invalid EntityType: ${value}`);
}

/**
 * Gets the current entityId from the router.
 */
export function getEntityInfo(params: RouteParamsGeneric) {
    const entityIds = Object.entries(params).filter(
        ([key, value]) => key.endsWith("Id") && value
    );

    const entityTypes = Object.entries(params).filter(
        ([key, value]) => key.endsWith("Type") && value
    );

    if (entityIds.length === 0) {
        // Check for Stek
        if (params.stekName) {
            const { steks } = useSteksManager();
            const entityType = getEntityType("STEK");
            const entityId = steks.value?.find(
                (stek) => stek.name === params.stekName
            )?.identity;
            return { entityId, entityType };
        } else {
            return;
        }
    }
    const [entityIdName, _entityId] = entityIds[entityIds.length - 1];

    let entityTypeKey: string | undefined;
    if (entityTypes.length === 0) {
        entityTypeKey = entityIdName.replace("Id", "");
    } else {
        entityTypeKey = `${entityTypes[entityTypes.length - 1][1]}`;
    }
    const entityId = `${_entityId}`;
    const entityType = getEntityType(snakeCase(entityTypeKey).toUpperCase());

    return { entityId, entityType };
}

export const useEntityStore = (
    entityType?: EntityType,
    entityId?: string,
    isCurrentEntity: boolean = false
) => {
    // Create a unique store ID for this entity
    const storeId = entityId ? `entity-${entityId}` : "entity";

    return defineStore(storeId, () => {
        const entity = ref<EntityInfo | null>();
        const isFetching = ref(false);
        const api = useServices({
            requires: {
                auth: true,
                organization: true,
                stek: false,
            },
        });
        const toast = useToast();
        const { findDomainByIdentity, findProductByIdentity } =
            useWorktreeStore();

        /**
         * If the store is for the current entity.
         */
        if (isCurrentEntity) {
            const route = useRoute();

            watch(
                () => route.params,
                async (newParams) => {
                    if (newParams) {
                        if (route.params.stekName) {
                            const { steks } = useSteksManager();
                            // Somehow `toBeUndefined` isn't available for this `until` call.
                            await until(steks).toMatch(
                                (_steks) => _steks !== undefined
                            );
                        }

                        const entityInfo = getEntityInfo(route.params);

                        if (!entityInfo?.entityId) {
                            entity.value = undefined;
                            return;
                        }

                        isFetching.value = true;

                        entityId = entityInfo.entityId;
                        entityType = entityInfo.entityType;

                        try {
                            entity.value = await getEntity();
                        } catch (e) {
                            if (
                                e instanceof DOMException &&
                                e.name === "AbortError"
                            ) {
                                // Request was aborted by the AbortController.
                                return;
                            } else {
                                // Don't show toast here as it's already shown in getEntity
                                console.error("Error fetching entity info:", e);
                            }
                        } finally {
                            isFetching.value = false;
                        }
                    }
                },
                { immediate: true }
            );
        }

        const controller = ref(new AbortController());

        let lastFetch: string;
        let lastResult: EntityInfo | undefined;

        /**
         * Get the entity for the current store.
         */
        const getEntity = async (force: boolean = false) => {
            // Avoid fetching again if the same entity is already fetched. This prevents double fetches upon loading
            // the router the very first time.
            if (!force && lastFetch === `${entityId}-${entityType}`) {
                return lastResult;
            }
            if (!entityId || !entityType) {
                return;
            }
            isFetching.value = true;

            const path = {
                entity_id: `${entityId}`,
            } as types.StartBizEntitiesEntityTypeEntityIdInfoGetData["path"];
            if (entityType) {
                path.entity_type = entityType;
            }

            // Abort any previously running requests and create a new controller
            controller.value.abort();
            controller.value = new AbortController();

            lastFetch = `${entityId}-${entityType}`;
            const handleEntityError = (error: unknown) => {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : typeof error === "string"
                          ? error
                          : "Failed to get entity info";

                toast.add({
                    severity: "error",
                    summary: "Entity Info Error",
                    detail: `Failed to load entity information: ${errorMessage}`,
                });
                console.error("Error fetching entity info:", error);
            };

            try {
                const { response, data: _entity } =
                    await api.startBizEntitiesEntityTypeEntityIdInfoGet({
                        path,
                        signal: controller.value.signal,
                    });

                if (!response.ok) {
                    handleEntityError(
                        response.statusText || "Failed to get entity info"
                    );
                    return;
                }

                lastResult = _entity;
                return _entity;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    handleEntityError(e);
                    throw e;
                }
            } finally {
                isFetching.value = false;
            }
        };

        // Find a specific entity in the ancestors.
        const findType = ({
            entityType,
        }: {
            entityType: EntityType;
        }): EntityInfo | undefined => {
            if (!entity.value) {
                return;
            }

            let parent: EntityInfo | null | undefined = entity.value;
            while (parent) {
                if (parent.entity_type === entityType) {
                    return parent;
                }
                parent = parent.parent;
            }
            return;
        };

        const computedEntity = ({ entityType }: { entityType: EntityType }) => {
            return computed(() => findType({ entityType }));
        };

        // Add some entity information to the domain - later on we can return
        // this kind of stuff from the new info endpoint.
        interface DomainEntity extends EntityInfo {
            products: ProductInTree[];
        }
        const domain = computed<DomainEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const domain = findType({ entityType: EntityType.DOMAIN });
            if (!domain) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'products' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'products' store and we do not want to have to do
            // another request.
            const domainInWorktree = findDomainByIdentity(domain.identity);
            return {
                ...domain,
                products: domainInWorktree ? domainInWorktree.products : [],
            } as DomainEntity;
        });

        // Add some entity information to the product - later on we can return
        // this kind of stuff from the new info endpoint.
        interface ProductEntity extends EntityInfo {
            processes: ProcessInTree[];
        }
        const product = computed<ProductEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const product = findType({ entityType: EntityType.PRODUCT });
            if (!product) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'processes' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'processes' store and we do not want to have to
            // do another request.
            const productInWorktree = findProductByIdentity(product.identity);
            return {
                ...product,
                processes: productInWorktree ? productInWorktree.processes : [],
            };
        });

        const findAncestorById = (identity: string): EntityInfo | undefined => {
            let currentEntity = entity.value;
            while (currentEntity) {
                if (identity === currentEntity.identity) {
                    return currentEntity;
                }
                currentEntity = currentEntity.parent;
            }
        };

        onMounted(async () => {
            // Actually get the Entity.
            entity.value = await getEntity();

            syncChannel.addEventListener(
                "message",
                async ({ data: { message, identity } }) => {
                    if (
                        message === "synced" &&
                        identity.attribute === "name" &&
                        findAncestorById(identity.entityId)
                    ) {
                        entity.value = await getEntity(true);
                    }
                }
            );

            useSyncStore().$onAction(({ name, after }) => {
                if (name === "sync") {
                    after(async (fullySyncedIdentities: SyncReturn) => {
                        if (
                            fullySyncedIdentities.some((identity) => {
                                if (identity.attribute !== "name") {
                                    return false;
                                }
                                return findAncestorById(identity.entityId);
                            })
                        ) {
                            entity.value = await getEntity(true);
                        }
                    });
                }
            });
        });

        return {
            entity,
            identity: computed(() => entity.value?.identity || "unknown"),
            name: computed(() => entity.value?.name || "unknown"),
            entityType: computed(() => entity.value?.entity_type || "unknown"),
            businessEntity: computedEntity({
                entityType: EntityType.BUSINESS_ENTITY,
            }),
            domain,
            product,
            role: computedEntity({ entityType: EntityType.ROLE }),
            process: computedEntity({ entityType: EntityType.PROCESS }),
            task: computedEntity({ entityType: EntityType.TASK }),
            gateway: computedEntity({ entityType: EntityType.GATEWAY }),
            flow: computedEntity({ entityType: EntityType.FLOW }),
            event: computedEntity({ entityType: EntityType.EVENT }),
            artefact: computedEntity({ entityType: EntityType.ARTEFACT }),
            stek: computedEntity({ entityType: EntityType.STEK }),
            organization: computedEntity({
                entityType: EntityType.ORGANIZATION,
            }),
            isFetching,
        };
    });
};

export const useEntityManager = (entityType: EntityType, entityId: string) =>
    storeToRefs(useEntityStore(entityType, entityId)());

// Helper to get store for current route's entity
export const useCurrentEntityStore = () => {
    return useEntityStore(undefined, undefined, true)();
};

export const useCurrentEntityManager = () =>
    storeToRefs(useCurrentEntityStore());
